import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { discoveryAPI, messagesAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Heart, MessageCircle, Activity, User, Settings, ArrowLeft, Send, Sparkles, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export const Messages = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [icebreakers, setIcebreakers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    loadMatches();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (matchId && matches.length > 0) {
      loadConversation(matchId);
    }
  }, [matchId, matches]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token || !WS_URL) return;

    try {
      wsRef.current = new WebSocket(`${WS_URL}/ws/${token}`);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        // Send ping every 30 seconds to keep connection alive
        setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message' && data.data.match_id === selectedMatch?.match_id) {
          setMessages(prev => [...prev, data.data]);
        } else if (data.type === 'typing' && data.match_id === selectedMatch?.match_id) {
          setIsTyping(true);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected, reconnecting...');
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const sendTypingIndicator = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedMatch) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        match_id: selectedMatch.match_id
      }));
    }
  };

  // Poll for new messages as fallback (when WebSocket is not available)
  useEffect(() => {
    if (!matchId) return;
    
    const interval = setInterval(async () => {
      // Only poll if WebSocket is not connected
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        try {
          const response = await messagesAPI.get(matchId);
          if (response.data.length > messages.length) {
            setMessages(response.data);
          }
        } catch (error) {
          // Silently fail
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [matchId, messages.length]);

  const loadMatches = async () => {
    try {
      const response = await discoveryAPI.getMatches();
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to load matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (id) => {
    const match = matches.find(m => m.match_id === id);
    setSelectedMatch(match);
    
    try {
      const response = await messagesAPI.get(id);
      setMessages(response.data);
      
      // Load icebreakers if no messages yet
      if (response.data.length === 0 && match) {
        const { aiAPI } = await import('../lib/api');
        const icebreakerRes = await aiAPI.getIcebreakers(match.user_id);
        setIcebreakers(icebreakerRes.data.icebreakers || []);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async (content = newMessage) => {
    if (!content.trim() || !matchId) return;
    
    setSending(true);
    try {
      const response = await messagesAPI.send({
        match_id: matchId,
        content: content.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      setIcebreakers([]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      // Send typing indicator on any other key press
      sendTypingIndicator();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Conversations List */}
      <div className={`w-full md:w-80 bg-white border-r border-gray-100 flex flex-col ${matchId ? 'hidden md:flex' : 'flex'}`}>
        <header className="p-4 border-b border-gray-100">
          <h1 className="font-display font-bold text-xl uppercase text-gray-900">Messages</h1>
        </header>
        
        <ScrollArea className="flex-1">
          {matches.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="font-body text-gray-500">No conversations yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/discover')}
              >
                Find Matches
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {matches.map((match) => (
                <button
                  key={match.match_id}
                  data-testid={`conversation-${match.match_id}`}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                    matchId === match.match_id ? 'bg-primary/5 border-l-2 border-primary' : ''
                  }`}
                  onClick={() => navigate(`/messages/${match.match_id}`)}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden relative">
                    <img
                      src={match.photos?.[0] || 'https://via.placeholder.com/100'}
                      alt={match.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <h3 className="font-display font-bold text-gray-900 truncate">{match.name}</h3>
                      {match.selfie_verified && (
                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    {match.last_message && (
                      <p className="font-body text-sm text-gray-500 truncate">
                        {match.last_message}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      {matchId ? (
        <div className="flex-1 flex flex-col h-screen bg-white">
          {/* Chat Header */}
          <header className="p-4 border-b border-gray-100 flex items-center gap-3 bg-white">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => navigate('/messages')}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Button>
            {selectedMatch && (
              <>
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={selectedMatch.photos?.[0] || 'https://via.placeholder.com/100'}
                    alt={selectedMatch.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <h2 className="font-display font-bold text-gray-900">{selectedMatch.name}</h2>
                    {selectedMatch.selfie_verified && (
                      <CheckCircle className="w-4 h-4 text-blue-500" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-body">
                    {selectedMatch.sports?.[0]?.sport || 'Active'}
                  </p>
                </div>
              </>
            )}
          </header>

          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4 bg-gray-50">
            <div className="max-w-2xl mx-auto space-y-4">
              {/* Icebreakers */}
              {icebreakers.length > 0 && messages.length === 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span className="font-body text-sm text-purple-700">AI Conversation Starters</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {icebreakers.map((icebreaker, index) => (
                      <button
                        key={index}
                        className="px-4 py-2 rounded-full bg-purple-50 border border-purple-100 text-sm font-body text-purple-700 hover:bg-purple-100 transition-colors"
                        onClick={() => sendMessage(icebreaker)}
                      >
                        {icebreaker}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              {messages.map((message, index) => {
                const isOwn = message.sender_id === user?.user_id;
                return (
                  <div
                    key={index}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                        isOwn
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100'
                      }`}
                    >
                      <p className="font-body text-sm">{message.content}</p>
                      <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
                        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[75%] px-4 py-2 rounded-2xl bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100">
                    <div className="flex items-center gap-1">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className="text-xs text-gray-400 ml-2">typing...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input */}
          <div className="p-4 border-t border-gray-100 bg-white">
            <div className="max-w-2xl mx-auto flex gap-2">
              <Input
                data-testid="message-input"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  sendTypingIndicator();
                }}
                onKeyPress={handleKeyPress}
                className="flex-1 bg-gray-50 border-gray-200"
                disabled={sending}
              />
              <Button
                data-testid="send-message-btn"
                onClick={() => sendMessage()}
                disabled={!newMessage.trim() || sending}
                className="bg-primary hover:bg-red-600"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
          <div className="text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="font-display text-xl text-gray-900 uppercase mb-2">Select a Conversation</h2>
            <p className="font-body text-gray-500">Choose a match to start chatting</p>
          </div>
        </div>
      )}
    </div>
  );
};
